
/*
 * Nettie's Native Box Builder v0.3
 *
 * - Reads flavors from Liquid-generated JSON
 * - Requires exactly the configured number of bites
 * - Rejects duplicate or malformed flavor IDs
 * - Writes one Shopify line-item property
 * - Keeps Add to Cart locked until the purchase
 *   controller is ready and the box is complete
 *
 * IMPORTANT:
 * The initial Shopify purchase button must also
 * be rendered disabled in Liquid. JavaScript alone
 * cannot provide a no-JavaScript purchase lock.
 */

(function () {
  'use strict';

  const VERSION = '0.3';

  function initialize(root) {
    if (root.dataset.initialized === 'true') return;

    const dataElement = document.getElementById(
      root.id.replace('netties-box-builder-', 'nbb-data-')
    );

    const form = document.getElementById(root.dataset.formId);

    const list = root.querySelector('.nbb-flavor-list');
    const count = root.querySelector('[data-nbb-count]');
    const remaining = root.querySelector('[data-nbb-remaining]');
    const progress = root.querySelector('.nbb-progress-track');
    const fill = root.querySelector('.nbb-progress-fill');
    const summaryCount = root.querySelector('[data-nbb-summary-count]');
    const summaryItems = root.querySelector('.nbb-summary-items');
    const status = root.querySelector('.nbb-status');

    if (!form) {
      console.error('[Netties Builder] Product form not found.');
      return;
    }

    const purchaseButtons = Array.from(
      form.querySelectorAll(
        'button[data-add-to-cart], button[type="submit"]'
      )
    );

    function lockPurchase() {
      purchaseButtons.forEach(button => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
    }

    // Lock immediately, before parsing or building the UI.
    lockPurchase();

    if (
      !dataElement ||
      !list ||
      !count ||
      !remaining ||
      !progress ||
      !fill ||
      !summaryCount ||
      !summaryItems ||
      !status
    ) {
      console.error('[Netties Builder] Required elements missing.');
      return;
    }

    let data;

    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('[Netties Builder] Invalid flavor JSON.', error);
      return;
    }

    const required = Number(data.required);

    if (!Number.isInteger(required) || required <= 0) {
      console.error('[Netties Builder] Invalid box quantity.');
      return;
    }

    const sourceFlavors = Array.isArray(data.flavors)
      ? data.flavors
      : [];

    const flavors = [];
    const seenIds = new Set();
    let configurationValid = true;

    sourceFlavors.forEach(flavor => {
      const id = typeof flavor.id === 'string'
        ? flavor.id.trim()
        : '';

      const name = typeof flavor.name === 'string'
        ? flavor.name.trim()
        : '';

      if (
        !id ||
        !name ||
        id.includes('|') ||
        id.includes(',') ||
        seenIds.has(id)
      ) {
        configurationValid = false;
        console.error('[Netties Builder] Invalid flavor:', flavor);
        return;
      }

      seenIds.add(id);

      flavors.push({
        id,
        name,
        description: flavor.description || '',
        image: flavor.image || null,
        order: Number(flavor.order) || 0
      });
    });

    if (!configurationValid || !flavors.length) {
      console.error(
        '[Netties Builder] Flavor configuration is invalid.'
      );
      return;
    }

    flavors.sort((a, b) => a.order - b.order);

    const formId = form.getAttribute('id');
    const flavorPropertyName = 'Select ' + required + ' Flavors';

    const flavorInput = document.createElement('input');

    flavorInput.type = 'hidden';
    flavorInput.name =
      'properties[' + flavorPropertyName + ']';
    flavorInput.setAttribute('form', formId);
    flavorInput.value = '';

    root.appendChild(flavorInput);

    const quantities = new Map(
      flavors.map(flavor => [flavor.id, 0])
    );

    function totalSelected() {
      let total = 0;

      quantities.forEach(quantity => {
        total += quantity;
      });

      return total;
    }

    function getSelections() {
      return flavors
        .filter(flavor => quantities.get(flavor.id) > 0)
        .map(flavor => ({
          id: flavor.id,
          name: flavor.name,
          quantity: quantities.get(flavor.id)
        }));
    }

    function serializeSelections() {
      return getSelections()
        .map(flavor => flavor.id + '|' + flavor.quantity)
        .join(',');
    }

    function validSelections() {
      const selected = getSelections();

      return (
        totalSelected() === required &&
        selected.length > 0 &&
        selected.every(flavor =>
          seenIds.has(flavor.id) &&
          Number.isInteger(flavor.quantity) &&
          flavor.quantity > 0 &&
          flavor.quantity <= required
        )
      );
    }

    function updatePurchaseState() {
      const ready =
        root.dataset.initialized === 'true' &&
        validSelections() &&
        window.nettiesPurchaseTest?.purchaseEnabled === true &&
        window.nettiesPurchaseTest?.isSubmitting?.() !== true;

      purchaseButtons.forEach(button => {
        button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));

        if (ready) {
          button.removeAttribute('title');
        } else {
          button.setAttribute(
            'title',
            'Complete your flavor selections first'
          );
        }
      });
    }

    function updateQuantity(id, delta) {
      if (!seenIds.has(id)) return;
      if (delta !== 1 && delta !== -1) return;

      const current = quantities.get(id);
      const next = current + delta;

      if (next < 0 || next > required) return;

      if (delta > 0 && totalSelected() >= required) {
        return;
      }

      quantities.set(id, next);
      refresh();
    }

    function makeButton(symbol, action, label) {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'nbb-step-button';
      button.textContent = symbol;
      button.setAttribute('aria-label', label);

      button.addEventListener('click', action);

      return button;
    }

    const rows = new Map();

    function makeFlavorRow(flavor) {
      const row = document.createElement('div');

      row.className = 'nbb-flavor-row';
      row.dataset.flavorId = flavor.id;

      const details = document.createElement('div');
      details.className = 'nbb-flavor-details';

      if (flavor.image) {
        const image = document.createElement('img');

        image.src = flavor.image;
        image.alt = '';
        image.loading = 'lazy';
        image.className = 'nbb-flavor-image';

        details.appendChild(image);
      }

      const copy = document.createElement('div');
      copy.className = 'nbb-flavor-copy';

      const name = document.createElement('strong');
      name.textContent = flavor.name;
      copy.appendChild(name);

      if (flavor.description) {
        const description = document.createElement('p');
        description.textContent = flavor.description;
        copy.appendChild(description);
      }

      details.appendChild(copy);

      const controls = document.createElement('div');
      controls.className = 'nbb-quantity-controls';

      const minus = makeButton(
        '−',
        () => updateQuantity(flavor.id, -1),
        'Remove one ' + flavor.name
      );
      minus.dataset.action = 'minus';

      const value = document.createElement('span');
      value.className = 'nbb-flavor-quantity';
      value.dataset.quantity = flavor.id;
      value.textContent = '0';

      const plus = makeButton(
        '+',
        () => updateQuantity(flavor.id, 1),
        'Add one ' + flavor.name
      );
      plus.dataset.action = 'plus';

      controls.append(minus, value, plus);
      row.append(details, controls);

      return row;
    }

    flavors.forEach(flavor => {
      const row = makeFlavorRow(flavor);
      rows.set(flavor.id, row);
      list.appendChild(row);
    });

    function refresh() {
      const total = totalSelected();
      const complete = validSelections();
      const left = required - total;

      count.textContent =
        total + ' of ' + required + ' selected';

      remaining.textContent = complete
        ? 'Your selections are complete'
        : left + (left === 1 ? ' bite' : ' bites') + ' to go';

      progress.setAttribute('aria-valuenow', String(total));

      fill.style.width =
        (Math.min(total, required) / required) * 100 + '%';

      summaryCount.textContent =
        total + ' / ' + required;

      root.classList.toggle('nbb-complete', complete);

      flavors.forEach(flavor => {
        const row = rows.get(flavor.id);
        const value = quantities.get(flavor.id);

        row.classList.toggle('nbb-has-selection', value > 0);

        row.querySelector('[data-quantity]').textContent =
          String(value);

        row.querySelector('[data-action="minus"]').disabled =
          value === 0;

        row.querySelector('[data-action="plus"]').disabled =
          total >= required;
      });

      summaryItems.replaceChildren();

      const selected = getSelections();

      if (!selected.length) {
        summaryItems.textContent =
          'Choose your first flavor to get started.';
      } else {
        selected.forEach(flavor => {
          const line = document.createElement('div');
          line.className = 'nbb-summary-line';

          const name = document.createElement('span');
          name.textContent = flavor.name;

          const quantity = document.createElement('strong');
          quantity.textContent = '× ' + flavor.quantity;

          line.append(name, quantity);
          summaryItems.appendChild(line);
        });
      }

      flavorInput.value = complete
        ? serializeSelections()
        : '';

      updatePurchaseState();
    }

    root.dataset.initialized = 'true';

    window.nettiesBoxBuilderPOC = {
      version: VERSION,
      required,

      getState() {
        return {
          required,
          selected: totalSelected(),
          valid: validSelections(),
          selections: getSelections(),
          allowedFlavorIds: flavors.map(flavor => flavor.id),
          propertyName: flavorPropertyName,
          propertyValue: flavorInput.value,
          formId
        };
      },

      refresh
    };

    refresh();

    console.info('[Netties Builder] v0.3 initialized', {
      required,
      flavorCount: flavors.length
    });
  }

  function start() {
    document
      .querySelectorAll('[data-nbb-builder]')
      .forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }

  // Reconcile purchase state when Safari restores
  // this page using the back/forward cache.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      window.nettiesBoxBuilderPOC?.refresh?.();
    }
  });
})();
